import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tabs, Spin, Alert, message } from 'antd';
import type { TabsProps } from 'antd';
import { UserSearchTable } from '../../components/admin/UserSearchTable';
import UserIndicatorsDashboard from '../../components/admin/UserIndicatorsDashboard';
import { UserDetailsModal } from '../../components/admin/UserDetailsModal';
import { listUsers, getIndicators, deleteUser } from '../../services/admin-users.service';

export const AdminUsers: React.FC = () => {
  const [activeTab, setActiveTab] = useState('2');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [tableData, setTableData] = useState<any>(null);

  const [indicatorsLoading, setIndicatorsLoading] = useState(false);
  const [indicatorsError, setIndicatorsError] = useState<string | null>(null);
  const [indicatorsData, setIndicatorsData] = useState<any>(null);
  const indicatorsRequestedRef = useRef(false);

  const [detailsUserId, setDetailsUserId] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listUsers({
        page,
        pageSize,
        search: search || undefined,
        role: selectedRole,
      });
      setTableData(response);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar usuários';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, selectedRole]);

  useEffect(() => {
    if (activeTab === '1') {
      fetchUsers();
    }
  }, [fetchUsers, activeTab]);

  useEffect(() => {
    if (activeTab !== '2') {
      indicatorsRequestedRef.current = false;
      return;
    }

    if (indicatorsData || indicatorsLoading || indicatorsRequestedRef.current) {
      return;
    }

    indicatorsRequestedRef.current = true;

    const fetchIndicators = async () => {
      setIndicatorsLoading(true);
      setIndicatorsError(null);
      try {
        const response = await getIndicators();
        setIndicatorsData(response);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar indicadores';
        setIndicatorsError(errorMsg);
        message.error(errorMsg);
      } finally {
        setIndicatorsLoading(false);
      }
    };

    fetchIndicators();
  }, [activeTab, indicatorsData, indicatorsLoading]);

  const handleDelete = async (userId: number, email: string) => {
    try {
      await deleteUser(userId);
      message.success(`Usuário ${email} excluído com sucesso`);
      // Refresh the current page; if it becomes empty, go back one page
      const isLastItemOnPage = tableData?.items?.length === 1 && page > 1;
      if (isLastItemOnPage) {
        setPage((p) => p - 1);
      } else {
        await fetchUsers();
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Erro ao excluir usuário');
      message.error(errorMsg);
      throw err;
    }
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Consulta de Usuários',
      children: (
        <div style={{ marginTop: 16 }}>
          {error && (
            <Alert
              message="Erro ao carregar usuários"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          <UserSearchTable
            loading={loading}
            data={tableData}
            onSearch={(newSearch) => {
              setSearch(newSearch);
              setPage(1);
            }}
            onRoleChange={(role) => {
              setSelectedRole(role);
              setPage(1);
            }}
            onPageChange={(newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            }}
            onDelete={handleDelete}
            onViewDetails={(userId) => {
              setDetailsUserId(userId);
              setDetailsOpen(true);
            }}
            search={search}
            selectedRole={selectedRole}
          />
        </div>
      ),
    },
    {
      key: '2',
      label: 'Indicadores de Usuários',
      children: (
        <div style={{ marginTop: 16 }}>
          {indicatorsError && (
            <Alert
              message="Erro ao carregar indicadores"
              description={indicatorsError}
              type="error"
              showIcon
              closable
              onClose={() => setIndicatorsError(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          <Spin spinning={indicatorsLoading}>
            <UserIndicatorsDashboard data={indicatorsData ?? undefined} loading={indicatorsLoading} />
          </Spin>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h1>Gestão de Usuários</h1>
      <Tabs items={items} activeKey={activeTab} onChange={setActiveTab} />
      <UserDetailsModal
        userId={detailsUserId}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />
    </div>
  );
};

export default AdminUsers;
